import React from 'react';
import { GlassCard, ProgressBar } from '@/design-system';
import { DollarSign, Target, TrendingUp, TrendingDown, CheckCircle, Zap, PieChart } from 'lucide-react';
import { formatNumber, formatPercentTrend } from '@/utils/formatters';

interface DashboardKPIs {
  totalInvestment: {
    dollars: number;
    teamWeeks: number;
    change: number;
  };
  q1Readiness: {
    percentage: number;
    readyFeatures: number;
    totalFeatures: number;
    status: 'on_track' | 'at_risk' | 'off_track';
  };
  activeEpics: {
    total: number;
    onTrack: number;
    atRisk: number;
    offTrack: number;
  };
  dependenciesAtRisk: {
    total: number;
    critical: number;
    high: number;
  };
}

interface KPICardsProps {
  kpis: DashboardKPIs | null;
  isLoading: boolean;
}

const KPICards: React.FC<KPICardsProps> = ({ kpis, isLoading }) => {

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {[...Array(5)].map((_, i) => (
          <GlassCard key={i} className="p-5" hover>
            <div className="animate-pulse space-y-3">
              <div className="flex items-center justify-between">
                <div className="h-3 bg-muted/50 dark:bg-muted/30 rounded w-24"></div>
                <div className="w-8 h-8 bg-muted/50 dark:bg-muted/30 rounded-lg"></div>
              </div>
              <div className="h-8 bg-muted/50 dark:bg-muted/30 rounded w-20"></div>
              <div className="h-3 bg-muted/50 dark:bg-muted/30 rounded w-16"></div>
            </div>
          </GlassCard>
        ))}
      </div>
    );
  }

  if (!kpis) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <GlassCard className="p-5 col-span-2 lg:col-span-5">
          <div className="text-center text-muted-foreground text-sm py-4">
            No data available
          </div>
        </GlassCard>
      </div>
    );
  }

  // Calculate category compliance percentage (mock for now - would come from backend)
  const categoryCompliancePercent = 94;
  // Calculate utilization percentage
  const utilizationPercent = kpis.totalInvestment.teamWeeks > 0 ? 87 : 0;

  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
      {/* Total Investment */}
      <GlassCard className="p-5" hover>
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Total Investment</span>
          <div className="w-8 h-8 rounded-lg bg-accent-blue-bg flex items-center justify-center">
            <DollarSign className="w-4 h-4 text-accent-blue" />
          </div>
        </div>
        <p className="text-3xl font-bold text-foreground" style={{ fontFeatureSettings: "'tnum' 1" }}>
          {formatNumber(kpis.totalInvestment.teamWeeks)}
        </p>
        <p className="text-sm text-muted-foreground mt-1">Team Weeks</p>
        {kpis.totalInvestment.change !== undefined && (
          <div className="flex items-center gap-1 mt-2 text-xs font-medium">
            {kpis.totalInvestment.change > 0 ? (
              <>
                <TrendingUp className="w-3 h-3 text-status-success" />
                <span className="text-status-success">{formatPercentTrend(kpis.totalInvestment.change)} from last year</span>
              </>
            ) : kpis.totalInvestment.change < 0 ? (
              <>
                <TrendingDown className="w-3 h-3 text-status-error" />
                <span className="text-status-error">{formatPercentTrend(kpis.totalInvestment.change)} from last year</span>
              </>
            ) : (
              <span className="text-muted-foreground">No change from last year</span>
            )}
          </div>
        )}
      </GlassCard>

      {/* Category Compliance */}
      <GlassCard className="p-5" hover>
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Category Compliance</span>
          <div className="w-8 h-8 rounded-lg bg-accent-violet-soft flex items-center justify-center">
            <CheckCircle className="w-4 h-4 text-accent-violet" />
          </div>
        </div>
        <p className="text-3xl font-bold text-accent-violet" style={{ fontFeatureSettings: "'tnum' 1" }}>
          {categoryCompliancePercent}%
        </p>
        <p className="text-sm text-muted-foreground mt-1">Within Target</p>
        <div className="mt-2">
          <ProgressBar
            value={categoryCompliancePercent}
            color="violet"
            size="sm"
                      />
        </div>
      </GlassCard>

      {/* Shovel Ready */}
      <GlassCard className="p-5" hover>
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Shovel Ready</span>
          <div className="w-8 h-8 rounded-lg bg-status-success-bg flex items-center justify-center">
            <Target className="w-4 h-4 text-status-success" />
          </div>
        </div>
        <p className="text-3xl font-bold text-status-success" style={{ fontFeatureSettings: "'tnum' 1" }}>
          {kpis.q1Readiness.percentage}%
        </p>
        <p className="text-sm text-muted-foreground mt-1">
          {kpis.q1Readiness.readyFeatures} of {kpis.q1Readiness.totalFeatures} Features
        </p>
        <div className="mt-2">
          <ProgressBar
            value={kpis.q1Readiness.percentage}
            color="emerald"
            size="sm"
                      />
        </div>
      </GlassCard>

      {/* Active EPICs */}
      <GlassCard className="p-5" hover>
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Active EPICs</span>
          <div className="w-8 h-8 rounded-lg bg-status-warning-bg flex items-center justify-center">
            <Zap className="w-4 h-4 text-status-warning" />
          </div>
        </div>
        <p className="text-3xl font-bold text-foreground" style={{ fontFeatureSettings: "'tnum' 1" }}>
          {kpis.activeEpics.total}
        </p>
        <p className="text-sm text-muted-foreground mt-1">
          {kpis.activeEpics.onTrack} Committed, {kpis.activeEpics.atRisk + kpis.activeEpics.offTrack} Stretch
        </p>
      </GlassCard>

      {/* Utilization */}
      <GlassCard className="p-5" hover>
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Utilization</span>
          <div className="w-8 h-8 rounded-lg bg-accent-cyan-soft flex items-center justify-center">
            <PieChart className="w-4 h-4 text-accent-cyan" />
          </div>
        </div>
        <p className="text-3xl font-bold text-accent-cyan" style={{ fontFeatureSettings: "'tnum' 1" }}>
          {utilizationPercent}%
        </p>
        <p className="text-sm text-muted-foreground mt-1">Capacity Allocated</p>
        <div className="mt-2">
          <ProgressBar
            value={utilizationPercent}
            color="cyan"
            size="sm"
                      />
        </div>
      </GlassCard>
    </div>
  );
};

export default KPICards;